(() => {
  if (window.mindelorVideoAutoplayInitialized) return;
  window.mindelorVideoAutoplayInitialized = true;

  const audioButtonSelector = "[data-mindelor-video-audio-toggle]";
  const autoplayVideoSelector = '[data-mindelor-video-autoplay="true"]';
  const frameObservers = new WeakMap();

  const getStage = (video) => video?.closest("[data-mindelor-video-stage]");

  const initializeFrame = (frame) => {
    if (frame.dataset.mindelorVideoFrameReady === "true") return;
    frame.dataset.mindelorVideoFrameReady = "true";

    let lastWidth = 0;
    let pendingWidthFrame = 0;
    const syncWidth = () => {
      const width = frame.getBoundingClientRect().width;
      if (width <= 0 || width === lastWidth) return;
      lastWidth = width;
      frame.style.setProperty("--mindelor-video-frame-width", `${width}px`);
    };
    syncWidth();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        if (pendingWidthFrame) return;
        pendingWidthFrame = window.requestAnimationFrame(() => {
          pendingWidthFrame = 0;
          syncWidth();
        });
      });
      observer.observe(frame);
      frameObservers.set(frame, {
        disconnect() {
          observer.disconnect();
          if (pendingWidthFrame) window.cancelAnimationFrame(pendingWidthFrame);
        }
      });
    }

    frame.querySelectorAll("video").forEach((video) => {
      const syncRatio = () => {
        const ratio = video.videoWidth / video.videoHeight;
        if (!Number.isFinite(ratio) || ratio <= 0) return;

        const variant = video.closest("[data-mindelor-video-variant]")?.dataset.mindelorVideoVariant;
        if (variant !== "mobile" && variant !== "desktop") return;
        frame.style.setProperty(`--mindelor-video-${variant}-ratio`, String(ratio));
        if (variant === "desktop" && !frame.querySelector('[data-mindelor-video-variant="mobile"]')) {
          frame.style.setProperty("--mindelor-video-mobile-ratio", String(ratio));
        }
      };
      video.addEventListener("loadedmetadata", syncRatio);
      video.addEventListener("resize", syncRatio);
      syncRatio();
    });
  };

  const setPlaybackState = (video, state) => {
    const stage = getStage(video);
    const media = video?.closest("video-media");

    if (stage) stage.dataset.mindelorVideoState = state;

    if (state === "playing") {
      media?.setAttribute("loaded", "");
      media?.setAttribute("playing", "");
      media?.removeAttribute("suspended");
    } else {
      media?.removeAttribute("playing");
    }

    (stage || media)?.dispatchEvent(
      new CustomEvent("mindelor:video-playback-state", { bubbles: true })
    );
  };

  const suppressNativeControls = (video) => {
    if (!video) return;

    video.controls = false;
    video.removeAttribute("controls");
  };

  const startPlayback = async (video) => {
    if (!video || document.visibilityState === "hidden" || video.offsetParent === null) return;

    suppressNativeControls(video);

    if (video.ended) video.currentTime = 0;
    if (!video.paused && video.currentTime > 0) {
      setPlaybackState(video, "playing");
      return;
    }

    if (getStage(video)) setPlaybackState(video, "pending");

    try {
      await video.play();
    } catch (_) {
      suppressNativeControls(video);
      setPlaybackState(video, "blocked");
    }
  };

  const initializeAutoplayVideo = (video) => {
    if (video.dataset.mindelorVideoAutoplayReady === "true") return;

    video.dataset.mindelorVideoAutoplayReady = "true";
    video.dataset.mindelorVideoAutoplay = "true";
    video.autoplay = true;
    video.defaultMuted = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "auto";
    video.setAttribute("autoplay", "");
    video.setAttribute("muted", "");
    video.setAttribute("loop", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("preload", "auto");
    suppressNativeControls(video);

    const fallback = getStage(video)?.querySelector(".mindelor-video-stage__fallback");
    if (fallback) {
      const restorePoster = () => {
        if (!video.poster || fallback.src === video.poster) return;
        fallback.removeAttribute("srcset");
        fallback.src = video.poster;
      };
      fallback.addEventListener("error", restorePoster);
      if (fallback.complete && fallback.naturalWidth === 0) restorePoster();
    }

    if (!getStage(video)) {
      new MutationObserver(() => suppressNativeControls(video)).observe(video, {
        attributes: true,
        attributeFilter: ["controls"]
      });
    }

    video.addEventListener("playing", () => setPlaybackState(video, "playing"));
    video.addEventListener("timeupdate", () => {
      if (!video.paused && video.currentTime > 0) setPlaybackState(video, "playing");
    });
    video.addEventListener("pause", () => setPlaybackState(video, "blocked"));
    video.addEventListener("error", () => setPlaybackState(video, "blocked"));
    video.addEventListener("ended", () => {
      video.currentTime = 0;
      startPlayback(video);
    });

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      startPlayback(video);
    } else {
      video.addEventListener("loadeddata", () => startPlayback(video), { once: true });
      video.addEventListener("canplay", () => startPlayback(video), { once: true });
    }
  };

  const updateButton = (button, video) => {
    const soundEnabled = Boolean(video && !video.muted && video.volume > 0);
    const label = button.querySelector("[data-mindelor-video-audio-label]");
    const mutedIcon = button.querySelector("[data-mindelor-video-muted-icon]");
    const soundIcon = button.querySelector("[data-mindelor-video-sound-icon]");

    button.setAttribute("aria-pressed", String(soundEnabled));
    button.setAttribute("aria-label", soundEnabled ? "Geluid uitzetten" : "Geluid aanzetten");
    if (label) label.textContent = soundEnabled ? "Geluid uit" : "Geluid aan";
    if (mutedIcon) mutedIcon.hidden = soundEnabled;
    if (soundIcon) soundIcon.hidden = !soundEnabled;
  };

  const isActivelyPlaying = (video) => {
    if (!video || video.offsetParent === null || video.paused || video.ended) return false;

    const stage = getStage(video);
    if (stage) return stage.dataset.mindelorVideoState === "playing";

    return video.closest("video-media")?.hasAttribute("playing") || false;
  };

  const initializeButton = (button) => {
    if (button.dataset.mindelorVideoAudioReady === "true") return;

    const container = button.closest(".content-over-media");
    const videos = Array.from(container?.querySelectorAll("video") || []);
    if (!videos.length) return;

    button.dataset.mindelorVideoAudioReady = "true";
    button.hidden = true;
    videos.forEach(initializeAutoplayVideo);

    const syncButton = () => {
      const activeVideo = videos.find(isActivelyPlaying);
      button.hidden = !activeVideo;
      if (activeVideo) updateButton(button, activeVideo);
    };

    container.addEventListener("mindelor:video-playback-state", syncButton);
    videos.forEach((video) => {
      video.addEventListener("volumechange", syncButton);
      video.addEventListener("playing", syncButton);
      video.addEventListener("pause", syncButton);
      video.addEventListener("ended", syncButton);
    });

    button.addEventListener("click", () => {
      const activeVideo = videos.find(isActivelyPlaying);
      if (!activeVideo) return;

      const shouldMute = !activeVideo.muted && activeVideo.volume > 0;
      videos.forEach((video) => {
        video.muted = shouldMute;
        if (!shouldMute && video.volume === 0) video.volume = 1;
      });

      if (!shouldMute) startPlayback(activeVideo);
      updateButton(button, activeVideo);
    });

    syncButton();
  };

  const initialize = (root = document) => {
    root.querySelectorAll("[data-mindelor-video-frame]").forEach(initializeFrame);
    root
      .querySelectorAll(
        ".shopify-section--video video-media[autoplay] video, .shopify-section--video [data-mindelor-video-stage] video"
      )
      .forEach(initializeAutoplayVideo);
    root.querySelectorAll(audioButtonSelector).forEach(initializeButton);
  };

  const resumeAutoplayVideos = () => {
    document.querySelectorAll(autoplayVideoSelector).forEach((video) => {
      if (video.offsetParent === null) {
        if (!video.paused) video.pause();
        return;
      }
      startPlayback(video);
    });
  };

  initialize();
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-mindelor-video-frame]").forEach((frame) => {
      frameObservers.get(frame)?.disconnect();
      frameObservers.delete(frame);
      delete frame.dataset.mindelorVideoFrameReady;
    });
  });
  document.addEventListener("DOMContentLoaded", () => {
    initialize();
    resumeAutoplayVideos();
  });
  document.addEventListener("visibilitychange", resumeAutoplayVideos);
  document.addEventListener("pointerdown", resumeAutoplayVideos, { once: true, capture: true });
  document.addEventListener("touchend", resumeAutoplayVideos, { once: true, capture: true });
  window.addEventListener("load", resumeAutoplayVideos);
  window.addEventListener("pageshow", resumeAutoplayVideos);
  window.matchMedia("(min-width: 700px)").addEventListener("change", resumeAutoplayVideos);
})();
